'use client';

import { useId } from 'react';
import { usePayPalCheckout } from './use-paypal-checkout';
import { AnimatedLoadingCircleIcon } from '@/components/icons/AnimatedLoadingCircleIcon';

interface PayPalButtonProps {
  amount: number;
  onApprove: (nonce: string) => void;
  onError: (error: Error) => void;
  onCancel?: () => void;
  disabled?: boolean;
}

/**
 * PayPal button component using Braintree PayPal Checkout.
 * Opens a popup window for PayPal authentication and returns a nonce on approval.
 */
export default function PayPalButton({
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  amount, // Amount is shown in order summary, not needed for vault flow
  onApprove,
  onError,
  onCancel,
  disabled = false,
}: PayPalButtonProps) {
  // Generate unique container ID for this instance
  const containerId = useId().replace(/:/g, '-') + '-paypal-btn';

  const { status, error } = usePayPalCheckout({
    containerId,
    onApprove,
    onCancel,
    onError: (err) => {
      onError(new Error(err.userMessage));
    },
  });

  return (
    <div className="mb-6 border rounded border-neutral-300">
      <div className="flex flex-row justify-between p-3 border-b border-neutral-300 bg-blue-50">
        {status === 'ready' && (
          <span className="font-semibold text-sm text-zinc-800">
            Pay with PayPal by clicking the button below
          </span>
        )}
        {(status === 'initializing' || status === 'idle') && (
          <span className="font-semibold text-sm text-zinc-800">Loading PayPal...</span>
        )}
      </div>

      <div id={containerId + '-wrapper'} className="px-3 py-6 pt-8">
        {/* Container for PayPal SDK to render buttons into */}
        <div
          id={containerId}
          className={`min-h-[52px] ${disabled ? 'pointer-events-none opacity-50' : ''}`}
        />

        {/* Error state with retry */}
        {status === 'error' && error && (
          <div className="text-center py-3">
            <p className="text-sm text-red-600 mb-2">{error.userMessage}</p>
            {error.recoverable && (
              <button
                type="button"
                onClick={() => window.location.reload()}
                className="text-sm text-blue-600 hover:text-blue-700 underline"
              >
                Refresh to try again
              </button>
            )}
          </div>
        )}

        <div className="min-h-8 flex flex-col items-center mt-2">
          {/* Loading state */}
          {status === 'tokenizing' && (
            <div className="flex items-center justify-center py-3">
              <AnimatedLoadingCircleIcon />
              <span className="ml-2 text-sm text-slate-500">Loading PayPal...</span>
            </div>
          )}

          {/* Help text */}
          {status === 'ready' && (
            <p className="text-xs text-slate-500 text-center">
              If PayPal window does not appear, disable any popup blockers.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
